import { useMemo, useRef } from 'react';
import { useInteraction } from '@/common/hooks/useInteraction';
import {
  useResolveTeamIdentityId,
  useResolveUserIdentityId,
} from '@/common/hooks/useResolveIdentityId';
import type { InteractionType } from '@/common/types/interaction';
import { useContactsData } from '@/views/Contacts/shared/useContactsData';
import { buildCoverSheetDocument } from '../document';
import {
  type SanitizedCoverSheetFormData,
  transformCoverSheetToFormData,
} from '../utils/transformCoverSheetToFormData';
import type { CoverSheetConfigData } from './useCoverSheetConfig';

/**
 * Assembles the cover-sheet document the form renders from.
 *
 * Reads the same SWR caches the cover-sheet write hooks revalidate, so a saved
 * field is reflected without an explicit refetch.
 */
export function useCoverSheetDocument(
  interactionId: string,
  interactionType: InteractionType,
  coverSheetConfig?: CoverSheetConfigData,
): { data: SanitizedCoverSheetFormData | undefined; loading: boolean } {
  const { data: interaction, isLoading: interactionLoading } = useInteraction(
    interactionId,
    interactionType,
  );
  // The document does not carry contacts, but the first paint still waits
  // for them so the contact cards mount populated instead of flashing
  // empty while their own fetch resolves.
  const { data: contacts, isLoading: contactsLoading } =
    useContactsData(interactionId);

  // Both resolve to `null` rather than erroring, so the form still renders if a
  // lookup fails — but the first paint waits for them, so a card gated on an
  // identity id doesn't flip from absent to present once they land.
  const { resolvedId: ownerUserIdentityId, isLoading: ownerIdLoading } =
    useResolveUserIdentityId(interaction?.owner);
  const { resolvedId: teamIdentityId, isLoading: teamIdLoading } =
    useResolveTeamIdentityId(interaction?.teamId);

  const data = useMemo(() => {
    if (!interaction || !contacts || !coverSheetConfig) return undefined;

    return transformCoverSheetToFormData(
      buildCoverSheetDocument(interaction, {
        ownerUserIdentityId,
        teamIdentityId,
      }),
    );
  }, [
    interaction,
    contacts,
    coverSheetConfig,
    ownerUserIdentityId,
    teamIdentityId,
  ]);

  // Identity resolution gates the first paint only. Changing the transaction
  // owner swaps in a new `/users/{userId}` key, and legacy ids miss the cache,
  // so re-gating on it would unmount the whole form and re-collapse its cards
  // right after the change.
  const hasLoadedOnce = useRef(false);
  const loading =
    interactionLoading ||
    contactsLoading ||
    !coverSheetConfig ||
    (!hasLoadedOnce.current && (ownerIdLoading || teamIdLoading));
  if (!loading) hasLoadedOnce.current = true;

  return {
    data,
    // `isValidating` is deliberately not part of this: it re-raises on every
    // focus and every post-save revalidation, flashing the form's skeleton.
    loading,
  };
}
